import type { AffordanceStateEntry } from "./affordance";
import type { Cell } from "./cell";
import type { AuraProfile, CombatantBase, MineProfile } from "./combatantBase";
import type { StatusInstance } from "./statuses";

/**
 * The live {@link Combatant} struct — one source-agnostic entity for every battlefield
 * thing (mech, NPC, pilot, object, deployable, drone, mine) — plus its tracked `current`
 * block and its disclosure level.
 *
 * A combatant is a frozen {@link CombatantBase} (snapshotted at spawn) plus a tracked
 * `current` plus active `statuses` layered over it. Rules code dispatches on **capability
 * presence** (`../model/capabilities`), not on the `kind` discriminator.
 */

/** uuid identifying a combatant within an encounter. */
export type CombatantId = string;

/**
 * What kind of source a combatant was spawned from. The reducer consults this only where
 * tracks genuinely differ; adding a new one is additive, not a reshape, because the rules
 * layer dispatches on capability predicates rather than on this discriminator.
 */
export type CombatantKind = "mech" | "npc" | "pilot" | "drone" | "deployable" | "object";


/**
 * The **tracked, mutable** combat values layered over the frozen base. Every manual command
 * that "changes a number" writes here (`set_hp`, `set_heat`), always clamped and validated
 * against the base's maxima by the reducer.
 */
export type CombatantCurrent = {
  /** Current HP, in `[0, base.maxHp]`. */
  hp: number;
  /**
   * Current heat, in `[0, base.maxHeat]`. **Absent** for kinds with no heat track
   * ({@link hasHeatTrack} false — mirroring {@link CombatantBase.maxHeat}'s absence, not a
   * `0` placeholder); the reducer rejects `set_heat` on those. Present whenever
   * `base.maxHeat` is present — the two fields are frozen and tracked together, though
   * reads still default `?? 0` defensively.
   */
  heat?: number;
  /**
   * Current **structure**, in `[0, base.maxStructure]` (rulebook "STRUCTURE DAMAGE").
   * Decremented by a `structure_delta` step when the combatant is reduced to 0 HP; at 0 it
   * is destroyed. **Absent** for a kind with no structure track ({@link hasStructureTrack}
   * false), not a `0` placeholder; reads default to `base.maxStructure ?? 0`. Present
   * whenever `base.maxStructure` is.
   */
  structure?: number;
  /**
   * Current **stress**, in `[0, base.maxStress]` (rulebook "OVERHEATING AND STRESS").
   * Decremented by a `stress_delta` step when the mech's heat exceeds its HEAT CAP; at 0 it
   * suffers a reactor meltdown. **Absent** for a kind with no stress track
   * ({@link hasStressTrack} false); reads default to `base.maxStress ?? 0`.
   */
  stress?: number;
  /**
   * **Marked burn** (rulebook "BURN"). Burn damage is dealt immediately *and* marked here;
   * at the **end** of the combatant's turn the mark ticks through an **ENGINEERING check**
   * (today `d20 + base.save` vs DC 10 — see `resolveBurnTick` in `@lancer-vtt/rules`, whose
   * comment records why that bonus is known-wrong and who owns the fix): on a **pass** all
   * marked burn clears and no damage is dealt; on a **fail** the full marked burn is dealt
   * (Overshield absorbs first, then HP, cascading into the Structure table) and the **mark
   * persists** into the next turn. Additional burn sources add to the mark.
   * Optional, defaulting to `0` at every read.
   */
  burn?: number;
  /**
   * **Per-combatant resources** — a flat, untyped bag of named counters keyed by free
   * string. This is the primitive that subsumes charges (1/round, 1/full-repair), weapon
   * `Limited {VAL}`, `Loading` (a reload gate), and the Apocalypse Die counter — everything
   * that is "a number on a combatant with a consume/reset rule". Mutated only by the
   * `resource_set`/`resource_delta` grounded steps and the trigger bus that resets them at
   * lifecycle points; the `resource` condition predicate reads it. Optional, defaulting to
   * `{}` (and each value `?? 0`) at every read. Values are floored at 0 by playback — a
   * resource never goes negative.
   */
  resources?: Record<string, number>;
  /**
   * **The per-affordance state ledger** — one entry per affordance (or weapon instance), keyed
   * by the affordance's `key` (`CombatantBase.affordances[].key`, written by `affordanceKey`)
   * or by a weapon's {@link weaponInstanceKey}. The single replacement for the deleted
   * `current.discharged` map plus the `scene_use:` / `limited:` prefixes of {@link resources}
   * (`decisions/0176`).
   *
   * The five old maps all had to agree on one hand-built string; folding the two state maps
   * into one entry per affordance means the spend and the discharge write the SAME slot the
   * frozen record names, with no second key derivation to get wrong.
   *
   * **`resources` was not drained.** Only the `scene_use:`/`limited:` prefixes moved here.
   * The structure-cascade counters, reaction caps, `Loading` gates and per-round mechanics stay
   * exactly where they are — `resources` remains the general-purpose counter bag it always was.
   *
   * **`discharged` is now the ONLY home for the Recharge flag** (`affordance-table` Phase 3.1):
   * `applyResolved`'s `discharge_set` case writes here and nowhere else, and every reader — the
   * pre-dice `checkWeaponRecharge`/`checkGrantedActionLegality` gates, `resolveRecharge`'s
   * start-of-turn roll, the two resolve-path idempotence guards, the frontend chooser predicates
   * — reads this one table for weapons and granted actions alike.
   *
   * Optional, defaulting to `{}` at every read, like every other tracked field here.
   */
  affordanceState?: Record<string, AffordanceStateEntry>;
}

/**
 * **Disclosure level** — how much of a combatant's stat block a player's client may be
 * shown, independent of spatial visibility. Monotonic, nested tiers: `"class"` (the floor —
 * class/template only, always visible for any visible hostile and so not actually gated by
 * this field) ⊂ `"loadout"` (adds weapons/systems/mounts) ⊂ `"full"` (adds the stat block —
 * max HP, SPD/EVA/ARM/skills — and, subject separately to
 * {@link EncounterState.showEnemyHp}, current HP). A scalar rather than independent
 * booleans, so "stats without loadout" is unrepresentable. See {@link Combatant.disclosure}.
 */
export type DisclosureLevel = "class" | "loadout" | "full";

/** The nesting order of {@link DisclosureLevel}s, for a raise-only write (the `reveal` atom). */
export const DISCLOSURE_RANK: Readonly<Record<DisclosureLevel, number>> = { class: 0, loadout: 1, full: 2 };

/** Whether a value is a known {@link DisclosureLevel} (the `set_disclosure` enum guard). */
export function isDisclosureLevel(v: unknown): v is DisclosureLevel {
  return v === "class" || v === "loadout" || v === "full";
}

/** See {@link Combatant.deployedFrom}. */
export type DeployedFrom = { combatantId: CombatantId; entryId: string; itemKey?: string };

/**
 * One combatant in an encounter: source-agnostic, `kind`-tagged, with a frozen `base` plus
 * a tracked `current` plus active `statuses`.
 *
 * `statuses` is a list of active {@link StatusInstance}s — each carrying a status name (a
 * free string, e.g. "Lock On", "Impaired") plus an `expiry` descriptor and an optional
 * applying `source`. Stored deduped **by name** (the reducer's `apply_status` rejects a
 * duplicate name) and order-insensitive. The status math reads names via
 * {@link statusNames}; the duration machinery reads `expiry`/`source`.
 *
 * `sourceRef` is an *optional* breadcrumb back to what was spawned (e.g. a frame id or NPC
 * class id) — purely informational, never re-resolved by the reducer, since the base is
 * frozen. Omit it if a driver has nothing useful to record.
 *
 * `pos` is the combatant's **anchor cell** on the map (where the VTT renders its token), or
 * `null` when it isn't on the map yet. Plain JSON, no class, and the anchor of the unit's
 * Size footprint — the footprint cells are *derived* from `(pos, base.size)`, never stored.
 * Only `move_token` writes it, and the reducer validates that a move is footprint-fitting,
 * unoccupied, and within the speed budget.
 */
export type Combatant = {
  id: CombatantId;
  kind: CombatantKind;
  name: string;
  base: CombatantBase;
  current: CombatantCurrent;
  /** Active status instances (name + expiry + source); deduped by name, order-insensitive. */
  statuses: StatusInstance[];
  /** Optional informational breadcrumb to the spawn source (frame/NPC class id). */
  sourceRef?: string;
  /**
   * The NPC **deployment tier** (Lancer tiers 1–3), captured once at spawn. Tier is
   * *identity*, not engine — the NPC control surface renders it as a badge, and it is the
   * source of truth rather than a fragile back-derivation from `saveTarget - 10`.
   * **NPC-only:** absent for `mech`/`pilot`/`object` combatants.
   */
  tier?: 1 | 2 | 3;
  /**
   * The NPC's applied **template ids** — identity, not engine. Set once at spawn from the
   * resolved template refs that actually contributed to the build (an id that didn't resolve
   * in the spawn driver's compendium honest-degrades and is never frozen). Read by the NPC
   * inspector's template badge and by activation grouping.
   *
   * **No parallel id list for chosen optional features** — the frozen features already
   * merged into {@link CombatantBase.weapons}/{@link CombatantBase.npcFeatures} are the
   * single source; an optional pick needs no separate identity marker once its feature is
   * frozen.
   *
   * **NPC-only, additive/optional:** absent for every non-NPC combatant and for an NPC
   * spawned with no templates.
   */
  templateIds?: string[];
  /** Anchor cell of the unit's footprint on the map, or `null` if unplaced. */
  pos?: Cell | null;
  /**
   * The **controlling slot**: the slot id that owns this combatant and is therefore
   * permitted to command it (move it, set its HP, attack with it, …). **Initialized at
   * spawn**, captured from the spawning pilot's `owner` (a player's mech → that player's
   * slot) or left `null` for a **GM-owned** combatant (NPCs, or a player-source mech with no
   * owner — the GM can act on anything, so an un-owned combatant is GM-only). This is a
   * snapshot of who controls the deployed combatant, not a live reference to the source
   * pilot: the source may be edited or deleted without changing control. The permission gate
   * (`canIssueEncounter`) reads *this*, never the pilot owner at check time, so the
   * encounter stays self-contained.
   *
   * **Not immutable after spawn**: the GM-only `set_controller` command reassigns this field
   * at any later point, mid-fight — a deliberate, logged, invertible re-home. Two
   * consequences worth knowing when reading code that touches this field: (1) other
   * capabilities *derived* from it at spawn — a drone's `base.allegiance`, see
   * {@link sideOf} — do NOT auto-follow a reassignment and must be corrected explicitly via
   * `set_controller`'s own optional `allegiance` field; (2) the owner-visibility exemptions
   * in the projection become reachable on a live reassignment, not just at spawn, which
   * overrides concealment/Hidden-omission and disclosure/`showEnemyHp` for the new owner.
   *
   * **Additive/optional**: absent reads as GM-owned (no player controls it), never a crash.
   */
  controllerSlotId?: string | null;
  /**
   * **The co-drivers** — additional slots that control this combatant alongside
   * {@link controllerSlotId}, the owner of record. Two players driving one unit is a real
   * table need that single-occupancy slots cannot express on their own.
   *
   * **Why two fields and not one flat list.** Every read of control splits into *"does slot
   * S control C?"* (a membership test, which a list answers natively) and *"which slot
   * **owns** C?"* (scalar-producing, so each site needs its own ruling). There are six of
   * the latter — resolution attribution, decider-slot prompt routing, drone/mine/object
   * deploy inheritance, ejected-pilot inheritance, and `pcCountFor`'s party-size bucket. A
   * flat list re-opens all of them to buy nothing, and at least two of the re-rulings would
   * be regressions (`pcCountFor` would count every co-driver as another party-size bucket).
   * The asymmetry is domain-true: a unit really does have an owner of record, and that is
   * exactly what attribution, party size, and inheritance key on.
   *
   * **Read it through {@link controlsCombatant}, never directly.** That predicate is the one
   * derivation for "does this slot control this unit"; reading this field by hand at a call
   * site is how the wall and the affordance drift apart.
   *
   * **The reducer normalizes on every write** (`set_controller`): deduped, and the owner of
   * record dropped if present — so this never contains {@link controllerSlotId}, and
   * `controlsCombatant` can never double-count. Do not assume a hand-built fixture is
   * normalized; assume anything the reducer produced is.
   *
   * **`controllerSlotId: null` with a NON-EMPTY co-list is legal, and it is a feature** —
   * "the GM owns it of record, players drive it," which is RAW's player-nominated allied NPC
   * (Core p.60). Don't add a reducer guard rejecting it.
   *
   * **A co-controller entry IS the "control grant" of `decisions/0179`** — the developer's
   * 2026-09-19 arbitrary-control ruling asked for "grant a slot the ability to drive this
   * unit, and change nothing else," and that is exactly what this field already does: it
   * never touches allegiance, side, activation phase, activation count, token colour or
   * the owner of record. Re-home (writing {@link controllerSlotId}/`base.allegiance`) and
   * grant (writing this list) stay unblended at the carrier and at the affordances; they
   * merely share the one `set_controller` wire command.
   *
   * **Control does NOT imply visibility** (`decisions/0179`, superseding the earlier
   * by-extension reading of `decisions/0154`). A co-controller of a concealed or
   * disclosure-masked hostile does **not** see it — full-sheet visibility is its own
   * grant, {@link visibilityGrantSlotIds}, read by `hasFullView`. The owner of record still
   * sees (that branch is pinned, and a unit on your own roster you cannot see is not a
   * state the table wants), but a *grant* does not. The stated consequence: **control
   * without EYES cannot fire** — a controlled-but-masked hostile renders a read-only
   * dossier plus a minimal turn strip, and its weapons stay unreachable until the view
   * grant lands. The GM affordance sets both by default.
   */
  coControllerSlotIds?: string[];
  /**
   * **The visibility grant** — slots that see this combatant in full, independent of
   * whether they can drive it. `decisions/0179`, developer ruling point 6: *visibility is
   * NOT derived from control.*
   *
   * **Read it through `hasFullView`, never directly** (the `controlsCombatant` discipline,
   * one field over): that predicate is the one derivation for "does this slot see this
   * unit in full," and it is the owner of record **OR** a member of this list.
   * {@link coControllerSlotIds} is deliberately NOT a member — that decoupling is the whole
   * point of the field, and re-adding the co-list at a call site silently undoes it.
   *
   * Exactly two sites consult it, both in `../projection`: `visibleTo` (the unit-level
   * concealment omission, which is ON THE WIRE) and
   * `projectCombatantFor` (the field-level `disclosure`/`showEnemyHp` ladder, display-only
   * per `decisions/0136`). A granted viewer gets the GM's projection **of that one unit**;
   * nothing changes for any other unit or viewer, and the ladder itself is untouched. This
   * is the narrowest form of the per-viewer disclosure `decisions/0081` deferred to "this
   * seam, later" — a per-slot full override, not a per-slot level.
   *
   * **The reducer normalizes on every write** (`set_controller`): **deduped only**. Unlike
   * the co-list, the owner of record is deliberately **NOT** dropped from this list. The
   * owner already sees via the predicate's other branch, so a redundant entry is harmless —
   * and dropping it would silently strip a player's view the moment a later `set_controller`
   * re-homes the unit away from them, which is the C1.4 hazard in a new coat.
   *
   * **Additive/optional**: absent reads as "nobody has been granted a view" (today's
   * behavior for every unit that predates the field), never a crash.
   */
  visibilityGrantSlotIds?: string[];
  /**
   * **Commit write-target breadcrumb** — the source mech's id (the `MechData.id`, which is
   * also the `PilotData.printedMechs` key). Set once at spawn alongside {@link pilotId}, and
   * used **only** as the target of the encounter→roster *commit* (the post-fight writeback
   * of HP/Structure/Stress plus Repair Cap remaining). NPCs and any non-mech-from-printed
   * combatant leave it unset ⇒ not committable.
   *
   * **A write target, NOT a live link** (mirroring `sourceRef`/`controllerSlotId`): the base
   * is frozen and never re-resolved from this id for *reads*. Commit **must tolerate a
   * dangling ref** — the mech may have been deleted or reprinted between deploy and commit,
   * in which case the commit skips and warns, never faults.
   */
  sourcePrintedMechId?: string;
  /**
   * **Commit write-target breadcrumb** — the source pilot's id (the owner of
   * {@link sourcePrintedMechId}'s mech in the roster). Set once at spawn; the commit
   * writeback names `printedMechs[mechId]` *under this pilot*. Same "write target, not a live
   * link" discipline as {@link sourcePrintedMechId}; unset for NPCs and non-printed
   * combatants.
   */
  pilotId?: string;
  /**
   * The source pilot's roster Bondmate (`PilotData.bondmate`), frozen at spawn. When the
   * combatant holds Bonded and another fielded combatant's {@link pilotId} matches, the referee
   * plants `Bondmate` on it partnered to that one (`referee/bondmatePlant.ts`). Unset for NPCs
   * and for a pilot with no Bondmate.
   */
  bondmatePilotId?: string;
  /**
   * **Commit write-target breadcrumb** for a keyed allied NPC — the `RosterState.npcWear` key
   * (`"rocket"`) its wear was seeded from at spawn and is written back to at commit
   * (`decisions/0196`). Set once at spawn, only when the deploy names a key; unset ⇒ an ordinary
   * ephemeral NPC the commit skips. Same "write target, not a live link" discipline as
   * {@link sourcePrintedMechId}.
   */
  persistentNpcKey?: string;
  /**
   * **GM concealment** — a broadcast-time **projection** fact, not a game-rule status:
   * `true` means every player's client is denied this combatant entirely (board, rails,
   * tracker, wire), while the GM still sees it in full. Read by the projection seam
   * `visibleTo`/`projectEncounterFor`.
   *
   * **Not toggled ONLY by the GM-only `set_concealed` command** — a hidden mine deploys
   * `concealed: true` and is later cleared by a **gameplay reveal step** (a successful
   * detection/disarm sweep, or detonation), a distinct, recorded, undoable resolved-command
   * step rather than GM fiat. Both are legitimate writers; don't assume every `concealed`
   * flip is a GM action.
   *
   * **Orthogonal to RAW `Hidden`** ({@link HIDDEN_STATUS}): Hidden is a *rules* status
   * (untargetable, but enemies know the approximate location per Core) and must NOT vanish a
   * token — concealment is a separate axis a unit can carry independently, in either
   * combination. Don't conflate the two.
   *
   * **Additive/optional**: absent (or `false`) means not concealed.
   */
  concealed?: boolean;
  /**
   * **GM manual disclosure level** — how much of this combatant's stat block a player's
   * client is allowed to see, independent of spatial visibility. **Orthogonal to
   * {@link concealed}**: concealed omits the WHOLE unit from a player's client; disclosure
   * redacts FIELDS of a unit that IS visible. The two gates stack; don't conflate them. A
   * single GLOBAL level shared by every player viewer, not per-scanner — a per-scanner map is
   * a strict superset addable later at the same projection seam.
   *
   * **Two writers converge on one mutation** ({@link withDisclosureLevel}): the GM's manual
   * `set_disclosure` command and a successful Scan's reveal step (`act_scan` rides
   * `apply_resolved`, gated on the scanning PC like any resolved-attack HP write).
   *
   * Read it through {@link disclosureOf}, never the raw field, so the default lives in one
   * place: an absent value reads as `"class"`, not as "unmasked".
   */
  disclosure?: DisclosureLevel;
  /**
   * **Features this unit has revealed by using them** (`decisions/0207`) — the `npcf_*` ids of the
   * weapons, systems and features a resolution has named for this combatant. Below `"loadout"`,
   * a player sees each listed feature's whole row as the loadout tier would show it, and the rest
   * stays locked; at `"loadout"`/`"full"` the set changes nothing, because everything is shown.
   *
   * **Per unit**: a second NPC of the same class keeps its own set. Written only by the
   * `features_revealed` resolved step, which the encounter room applies after a resolution names
   * the feature; undoing that action restores the prior set. Monotonic in play otherwise, and no
   * GM command writes it (`set_disclosure` is the override). Sparse: absent means none revealed,
   * never an empty array (see {@link withRevealedFeatures}).
   */
  revealedFeatureIds?: string[];
  /**
   * **GM-set token image** — a server-relative image URL the board token shows in place of its
   * resolved art (`projects/token-art.md` Phase 3). A display field like {@link name}: written only
   * by the GM-only `set_token_image` command, read by every client and the CLI without roster
   * access. It is an override, not a spawn snapshot: a mech without one keeps resolving its art
   * live through the roster (`tokenImageFor` in `frontend/src/lib/tokenArt.ts`).
   *
   * **Masked tiers drop it.** It is absent from the projection's `MaskedKeepFields`, so a player
   * who sees this unit through a disclosure mask never gets it at render time; like every other
   * field, it still rides the wire (`decisions/0136`: field masking is display-only).
   *
   * **Additive/optional**: absent means "no GM override".
   */
  tokenImage?: string;
  /**
   * **Object section grouping** — RAW authors a multi-section object (a SIZE-3 wall) as N
   * independently-destroyable SIZE-1 sections, each its own combatant, but the *authoring
   * surface* places and deletes them as one thing. This is the shared identity that binds
   * those sections together: every section of the same authored object carries the same
   * `objectGroupId`, minted by the group-spawn helper and never derived from the combatant
   * id. {@link objectGroupMembers} is the query that reads it back.
   *
   * **A top-level field, deliberately NOT folded into `objectProfile`.** That profile mirrors
   * `TerrainProperties`'s *spatial* flag vocabulary; grouping is an identity/authoring concern, not
   * a spatial contribution, so it gets its own field rather than growing that vocabulary.
   *
   * **`object`/`deployable`-only in practice** (nothing stops another kind from carrying it,
   * but nothing sets it): a monolithic SIZE-n object (one token, `10×n` HP) has no group — it
   * is just a combatant with `size: n`. Absent for every ungrouped combatant.
   */
  objectGroupId?: string;
  /**
   * **Which system deployed this combatant** (`ll1-drones` D1, `decisions/0201`) — stamped by a
   * content `deploy_object`/`deploy_mine` fired from a carried affordance. The typed link every
   * deployer-relative read takes; never parse {@link objectGroupId} for it. `combatantId` is the
   * deployer, `entryId` the overlay entry that deployed it, and `itemKey` the deployer's frozen
   * system instance (`FrozenSystem.instanceId`) when the entry is an equipped system — the key a
   * destroyed drone stamps into the deployer's destroyed-item ledger. Absent for a GM-authored
   * object and for anything deployed outside an activation.
   */
  deployedFrom?: DeployedFrom;
  /**
   * **Map-asset catalogue id** (`decisions/0193`) — the `MAP_ASSETS` entry this object was
   * spawned from (`spawnAsset`). The display name and the board art key off it; the mechanics do
   * not, because they were snapshotted into `base` at spawn. A top-level identity field, like
   * {@link objectGroupId}. A destroyed asset is unplaced by the destroy-consequence sweep, which
   * also paints its `objectProfile.remains`.
   *
   * An id the catalogue no longer knows still loads: it loses only the art, which falls back to
   * the plain object token. Absent for every combatant not spawned from the catalogue.
   */
  assetId?: string;
  /**
   * **Mine arm state + detonation payload** — see {@link MineProfile} for why this is a
   * top-level field rather than living inside the frozen {@link CombatantBase} (its `armed`
   * sub-field mutates post-spawn). **`deployable`-only in practice**: absent for every
   * non-mine combatant.
   */
  mineProfile?: MineProfile;
  /** A deployed drone's aura — see {@link AuraProfile}. */
  auraProfile?: AuraProfile;
}

/**
 * A combatant's manual GM disclosure level, read through this seam rather than the raw
 * field. A never-set combatant reads as `"class"` — RAW p.283's always-public class/template
 * floor, the mechanically-honest default for a hostile no one has explicitly disclosed or
 * Scanned. This is the one seam that makes every non-explicitly-disclosed hostile
 * masked-by-default for a player viewer; the raw field is untouched, since an explicit
 * `set_disclosure`/Scan write still lands verbatim.
 */
export function disclosureOf(c: Pick<Combatant, "disclosure">): DisclosureLevel {
  return c.disclosure ?? "class";
}

/**
 * Write a combatant's `disclosure` to `level` — the ONE mutation both writers converge on:
 * the GM's manual `set_disclosure` reducer case and a successful Scan's reveal step. Pure,
 * and deliberately does no existence/permission/enum validation — that is each caller's own
 * job, and the two legitimately differ (the reducer case rejects an unknown combatant id and
 * a bad enum value before calling this; the resolved-step applier instead no-ops on a gone
 * target, mirroring `concealed_set`'s replay-total discipline).
 */
export function withDisclosureLevel(c: Combatant, level: DisclosureLevel | undefined): Combatant {
  return { ...c, disclosure: level };
}

/**
 * Write a combatant's {@link Combatant.revealedFeatureIds} to `ids`, keeping the field sparse: an
 * `undefined` or empty set removes the key rather than storing `[]`, so a reveal followed by its
 * undo leaves the combatant byte-identical to before.
 */
export function withRevealedFeatures(c: Combatant, ids: readonly string[] | undefined): Combatant {
  const { revealedFeatureIds: _prev, ...rest } = c;
  return ids === undefined || ids.length === 0 ? rest : { ...rest, revealedFeatureIds: [...ids] };
}
